import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Alert, AlertDescription, AlertTitle, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@clouway/ui'

import { ApiError } from '@/api'
import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import { roleDisplayName } from '@/features/users'
import { RoleForm, type RoleFormValues } from './RoleForm'
import { TierMatrix } from './TierMatrix'
import { buildMembership, deriveStates, residueMembership } from '../transform/membership'
import type { CatalogRole, DomainState, ProductStructure, RoleInput } from '../types'

const buildSchema = (requiredMessage: string) =>
  z.object({
    id: z
      .string()
      .min(1, requiredMessage)
      .regex(/^[a-z][a-z0-9_]*$/, requiredMessage),
    description: z.string(),
  })

// Combines the id/description fields, the clone-from-preset picker and the
// tier matrix into one editor, used by both RegisterRole and EditRole (and,
// with `readOnly`, as the preset detail view). Owns the single
// useForm/handleSubmit call — the matrix's per-domain state lives beside it
// in plain useState since it isn't text-input shaped, and both are combined
// into the submitted RoleInput only at submit time (see buildMembership).
export function RoleEditor({
  structure,
  presets,
  mode,
  initialRole,
  readOnly = false,
  onSubmit,
}: {
  structure: ProductStructure
  presets: CatalogRole[]
  mode: 'create' | 'edit'
  initialRole?: CatalogRole
  readOnly?: boolean
  onSubmit: (input: RoleInput) => void | Promise<void>
}) {
  const { t, i18n } = useTranslation('feature')
  const schema = useMemo(() => buildSchema(t('role.form.required')), [t])
  const { control, handleSubmit, formState, setError } = useForm<RoleFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { id: initialRole?.id ?? '', description: initialRole?.description ?? '' },
  })

  const initialMembership = useMemo(
    () => ({ scopes: initialRole?.scopes ?? [], permissions: initialRole?.permissions ?? [] }),
    [initialRole],
  )
  const [states, setStates] = useState<Record<string, DomainState>>(() => deriveStates(structure, initialMembership))
  const [matrixDirty, setMatrixDirty] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSucceeded, setSubmitSucceeded] = useState(false)

  // Grants the current catalog can't express any more (dropped resource/verb,
  // or a scope orphaned of its permission) but that the role still carries.
  // Only the translatable share (`preserved`) can actually be folded back in
  // unchanged on submit — the server validates scope ids on write, so a scope
  // whose resource the structure no longer declares at all (`dropped`) can
  // never be resubmitted and is lost on save. Both are surfaced so the admin
  // isn't left wondering why the matrix doesn't show everything, or why a
  // grant quietly vanished.
  const residue = useMemo(() => residueMembership(structure, states, initialMembership), [structure, states, initialMembership])
  const preservedResidueCount = residue.scopes.preserved.length + residue.permissions.length
  const droppedResidueCount = residue.scopes.dropped.length

  const onDomainChange = (domain: string, state: DomainState) => {
    setStates((prev) => ({ ...prev, [domain]: state }))
    setMatrixDirty(true)
  }

  const applyPreset = (presetId: string) => {
    const preset = presets.find((candidate) => candidate.id === presetId)
    if (!preset) return
    setStates(deriveStates(structure, { scopes: preset.scopes, permissions: preset.permissions }))
    setMatrixDirty(true)
  }

  const submit = async (values: RoleFormValues) => {
    setSubmitError(null)
    const membership = buildMembership(structure, states, initialMembership)
    try {
      await onSubmit({ id: values.id, description: values.description, scopes: membership.scopes, permissions: membership.permissions })
      // Only a resolved onSubmit counts as success. react-hook-form's own
      // isSubmitSuccessful flips true whenever handleSubmit's callback
      // returns without throwing — which a caught-and-swallowed error below
      // also does — so the unsaved-changes guard (gated on this flag, not
      // that one) would otherwise stop guarding right after a FAILED save.
      setSubmitSucceeded(true)
    } catch (error) {
      // A duplicate id is a legitimate server response (409), not a bug —
      // point it at the id field the same way a client-side validation
      // error would render. Anything else surfaces as a form-level alert;
      // either way we stay on the page so the matrix state isn't lost.
      if (error instanceof ApiError && error.status === 409) {
        setError('id', { type: 'server', message: t('role.form.duplicateId') })
        return
      }
      setSubmitError(error instanceof ApiError ? error.message : t('role.form.submitError'))
    }
  }

  if (readOnly) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
        <div>
          <h2 className="text-sm font-medium">{initialRole ? roleDisplayName(initialRole, i18n.language) : ''}</h2>
          <p className="text-muted-foreground font-mono text-xs">{initialRole?.id}</p>
        </div>
        <TierMatrix structure={structure} states={states} onChange={() => {}} readOnly />
        <p className="text-muted-foreground text-xs">{t('role.matrix.readOnlyCaption')}</p>
      </div>
    )
  }

  const creating = mode === 'create'

  return (
    <form className="mx-auto flex w-full max-w-3xl flex-col gap-4" onSubmit={handleSubmit(submit)}>
      {/* Mirrors ServiceAccountForm/UserForm: submitSucceeded only flips true
          AFTER onSubmit resolves, but the screens that own this editor
          navigate away from INSIDE onSubmit — so without also checking
          isSubmitting the guard would still be armed (and intercept that
          navigation) at the moment it fires. RHF sets isSubmitting=true and
          re-renders before invoking onSubmit, so the guard is already
          disarmed by the time onSubmit's navigate() runs; a failed submit
          flips isSubmitting back to false and re-arms it. */}
      <UnsavedChangesGuard when={(formState.isDirty || matrixDirty) && !formState.isSubmitting && !submitSucceeded} />

      {submitError && (
        <Alert variant="destructive">
          <AlertTitle>{t('role.form.submitErrorTitle')}</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <RoleForm control={control} mode={mode} />

      {presets.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t('role.form.clonePreset.label')}</span>
          <Select value="" onValueChange={applyPreset}>
            <SelectTrigger className="w-72" aria-label={t('role.form.clonePreset.label')}>
              <SelectValue placeholder={t('role.form.clonePreset.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {presets.map((preset) => (
                <SelectItem key={preset.id} value={preset.id}>
                  {roleDisplayName(preset, i18n.language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <TierMatrix structure={structure} states={states} onChange={onDomainChange} />

      {preservedResidueCount > 0 && (
        <p className="text-muted-foreground text-xs">{t('role.matrix.residueNotice', { count: preservedResidueCount })}</p>
      )}

      {droppedResidueCount > 0 && (
        <Alert variant="warning">
          <AlertTitle>{t('role.matrix.residueDroppedTitle')}</AlertTitle>
          <AlertDescription>{t('role.matrix.residueDroppedNotice', { count: droppedResidueCount })}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting ? t(creating ? 'role.form.creating' : 'role.form.submitting') : t(creating ? 'role.form.create' : 'role.form.submit')}
      </Button>
    </form>
  )
}
